/**
 * Dialog de catégorisation des transactions Wave hors décaissement chauffeur.
 * (Retrait Express, Transport, Dépôt de cash, etc.)
 * - Sélection d'une catégorie existante (défauts + personnalisées)
 * - Ajout inline d'une nouvelle catégorie
 * - Les tx catégorisées passent en "Hors flux" (non_concernee_wave)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Tag, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaveCategorizeDialog = ({ open, onClose, txIds = [], txLabel, onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState('');
  const [newCat, setNewCat] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setSelected(''); setNewCat(''); setShowAdd(false); return; }
    axios.get(`${API}/wave/categories`)
      .then(r => setCategories(r.data.items || []))
      .catch(() => toast({ title: 'Erreur chargement catégories', variant: 'destructive' }));
  }, [open]);

  const addCategory = async () => {
    const name = newCat.trim();
    if (name.length < 2) { toast({ title: 'Nom trop court (min 2 caractères)', variant: 'destructive' }); return; }
    setAddingCat(true);
    try {
      const r = await axios.post(`${API}/wave/categories`, { name });
      if (!categories.includes(r.data.name)) setCategories([...categories, r.data.name]);
      setSelected(r.data.name);
      setNewCat('');
      setShowAdd(false);
      toast({ title: r.data.created ? `Catégorie « ${r.data.name} » créée` : `Catégorie « ${r.data.name} » déjà existante` });
    } catch (e) {
      toast({ title: 'Erreur création catégorie', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setAddingCat(false); }
  };

  const submit = async () => {
    if (!selected) { toast({ title: 'Sélectionnez une catégorie', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/wave/transactions/categorize`, {
        transactionIds: txIds,
        category: selected,
      });
      toast({
        title: `${r.data.updated} transaction(s) catégorisée(s) → ${selected}`,
        description: r.data.skippedLocked > 0 ? `${r.data.skippedLocked} ignorée(s) (mois verrouillé)` : 'Sorties du flux de rapprochement',
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur catégorisation', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="wave-categorize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-teal-600" />
            Catégoriser {txIds.length > 1 ? `${txIds.length} transactions` : 'la transaction'}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {txLabel ? <span className="font-mono">{txLabel} — </span> : null}
            Pour les mouvements qui ne sont pas des décaissements chauffeur (Retrait Express, Dépôt de cash…).
            La transaction sortira du flux de rapprochement, avec la catégorie tracée.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setSelected(c)}
                data-testid={`wave-category-option-${c}`}
                className={`px-3 py-2.5 rounded-lg border text-left text-[13px] font-medium transition-colors ${
                  selected === c
                    ? 'bg-teal-50 border-teal-400 text-teal-800 ring-1 ring-teal-300'
                    : 'bg-white border-zinc-200 text-zinc-700 hover:border-teal-200 hover:bg-teal-50/40'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          {showAdd ? (
            <div className="flex items-center gap-2">
              <Input
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                placeholder="Nom de la nouvelle catégorie…"
                className="text-sm h-9"
                data-testid="wave-new-category-input"
                onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                autoFocus
              />
              <Button size="sm" onClick={addCategory} disabled={addingCat} data-testid="wave-new-category-submit" className="h-9">
                {addingCat ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="text-[12px] text-teal-700 hover:text-teal-900 font-semibold flex items-center gap-1"
              data-testid="wave-new-category-toggle"
            >
              <Plus className="h-3.5 w-3.5" /> Ajouter une catégorie
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving || !selected}
                  className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="wave-categorize-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Tag className="h-4 w-4 mr-2" />}
            Catégoriser
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WaveCategorizeDialog;
